import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getBillingConfig, openCheckout, readPendingPlan, clearPendingPlan, stashPendingPlan } from "@/lib/paddle";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * /checkout/confirm — post-auth pre-checkout confirmation.
 * Displays plan, billing frequency, price, currency, tax note, trial,
 * first-payment date, renewal behaviour and links to cancel + refund
 * policies. Only when the user clicks "Continue to secure checkout"
 * does Paddle Overlay open.
 */
export default function CheckoutConfirm() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [cfg, setCfg] = useState(null);
    const [plan, setPlan] = useState(null);
    const [billing, setBilling] = useState(params.get("billing") || "monthly");
    const [busy, setBusy] = useState(false);
    const [launchEmail, setLaunchEmail] = useState(user?.email || "");
    const [launchSent, setLaunchSent] = useState(false);

    const slug = params.get("plan") || readPendingPlan()?.plan || "essential";

    useEffect(() => {
        getBillingConfig().then((c) => {
            setCfg(c);
            const p = (c.plans || []).find((x) => x.internal_plan_slug === slug);
            setPlan(p);
        });
    }, [slug]);

    // Persist choice in case user navigates away
    useEffect(() => { stashPendingPlan({ plan: slug, billing }); }, [slug, billing]);

    if (!user?.id) {
        return (
            <div className="max-w-lg mx-auto p-8 text-center">
                <p className="text-sm text-stone-500">Please sign in to continue.</p>
                <Link to={`/login?next=${encodeURIComponent(`/checkout/confirm?plan=${slug}&billing=${billing}`)}`} className="mt-3 inline-block underline">Sign in</Link>
            </div>
        );
    }

    if (!cfg || !plan) {
        return (
            <div className="max-w-lg mx-auto p-8 flex items-center gap-2 text-sm text-stone-500">
                <Loader2 size={14} className="animate-spin" /> Loading your plan…
            </div>
        );
    }

    const priceId = billing === "annual" ? plan.paddle_annual_price_id : plan.paddle_monthly_price_id;
    const canCheckout = cfg.checkout_configured && priceId;

    const start = async () => {
        if (!canCheckout) return;
        setBusy(true);
        try {
            await openCheckout({
                priceId, userId: user.id, planSlug: slug, email: user.email,
                onClose: () => setBusy(false),
                onEvent: (e) => {
                    if (e?.name === "checkout.completed") {
                        clearPendingPlan();
                        setTimeout(() => nav("/checkout/success"), 400);
                    }
                },
            });
        } catch (e) {
            toast.error("Checkout could not open. Please try again.");
            setBusy(false);
        }
    };

    const joinLaunchList = async () => {
        if (!launchEmail) return;
        try {
            await api.post("/billing/launch-list", { email: launchEmail, plan_slug: slug, billing });
            setLaunchSent(true);
            toast.success("You're on the launch list. We'll email you the moment payments open.");
        } catch { toast.error("Could not save your email."); }
    };

    return (
        <div className="max-w-2xl mx-auto p-6 md:p-8" data-testid="checkout-confirm">
            <Link to="/pricing" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"><ArrowLeft size={12} /> Back to pricing</Link>
            <h1 className="mt-3 text-2xl font-bold tracking-tight">Confirm your subscription</h1>
            <p className="text-sm text-stone-500 mt-1">Review the details below. Nothing is charged until you complete Paddle&apos;s secure checkout.</p>

            <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 space-y-3" data-testid="checkout-summary">
                <Row k="Plan" v={plan.display_name || slug} />
                <Row k="Billing frequency">
                    <div className="flex gap-2">
                        {["monthly", "annual"].map((b) => (
                            <button key={b} onClick={() => setBilling(b)}
                                data-testid={`billing-${b}`}
                                className={`text-xs px-3 py-1 rounded-full border ${billing === b ? "bg-stone-900 text-white border-stone-900" : "border-stone-300"}`}>
                                {b === "monthly" ? "Monthly" : "Annual"}
                            </button>
                        ))}
                    </div>
                </Row>
                <Row k="Currency" v={plan.currency || "USD"} />
                <Row k="Trial" v={plan.trial_days ? `${plan.trial_days} days free` : "No trial"} />
                <Row k="First payment" v={plan.trial_days ? `After ${plan.trial_days}-day trial ends` : "Immediately after checkout"} />
                <Row k="Renewal" v={`Auto-renews ${billing === "annual" ? "yearly" : "monthly"} until cancelled`} />
                <Row k="Taxes" v="Calculated by Paddle at checkout based on your location" />
            </div>

            <div className="mt-5 text-xs text-stone-500 leading-relaxed" data-testid="checkout-payment-disclosure">
                Payments for VAULT subscriptions are securely processed by <b>Paddle</b>, acting as Merchant of Record.
                VAULT does not receive or store your complete card number or card security code. See{" "}
                <Link to="/billing-terms" className="underline">Subscription &amp; Billing Terms</Link> and{" "}
                <Link to="/refund-policy" className="underline">Refund &amp; Cancellation Policy</Link>. Cancel anytime from Settings → Plan &amp; Billing.
            </div>

            {canCheckout ? (
                <Button className="mt-6 w-full" size="lg" onClick={start} disabled={busy} data-testid="checkout-start-btn">
                    {busy ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Opening secure checkout…</>) : (<><ShieldCheck size={14} className="mr-1.5" /> Continue to secure Paddle checkout</>)}
                </Button>
            ) : (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="payments-coming-soon">
                    <div className="text-xs font-semibold uppercase tracking-wider text-amber-800">Payments coming soon</div>
                    <p className="text-sm text-stone-700 mt-1.5">
                        VAULT is finishing its billing setup with Paddle. Leave your email and we&apos;ll invite you the moment payments open for the <b>{plan.display_name || slug}</b> plan.
                    </p>
                    {launchSent ? (
                        <div className="mt-3 text-sm text-emerald-700 font-medium" data-testid="launch-list-thanks">You&apos;re on the list — check your inbox for updates.</div>
                    ) : (
                        <div className="mt-3 flex gap-2">
                            <input
                                type="email" value={launchEmail} onChange={(e) => setLaunchEmail(e.target.value)}
                                data-testid="launch-list-email"
                                placeholder="you@company.com"
                                className="flex-1 rounded-md border border-amber-300 bg-white px-3 py-2 text-sm"
                            />
                            <Button onClick={joinLaunchList} data-testid="launch-list-submit">Notify me</Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Row({ k, v, children }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="text-xs text-stone-500">{k}</div>
            <div className="text-sm font-medium text-stone-900">{v ?? children}</div>
        </div>
    );
}
